import React, { useCallback, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import apiFetch from "@wordpress/api-fetch";
import AsyncSelect from "react-select/async";
import classNames from "classnames";
import debounce from "lodash/debounce";
import VippsLogo from "../assets/images/vipps.svg";
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input/input';
import nb from 'react-phone-number-input/locale/nb';
import Select from 'react-select';

export default function MembershipForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessQuery, setBusinessQuery] = useState("");
  const [schoolQuery, setSchoolQuery] = useState("");
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
	setValue,
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      TypeProsess: "LTR",
      Medlemskategori: localStorage.getItem("ffb-membership-type")
        ? localStorage.getItem("ffb-membership-type")
        : "ORD",
      mobile: localStorage.getItem("ffb-membership-cta-phone-number"),
      SamtykkeNyhetsbrev: true
    }
  });

  /**
   * Process form submission.
   *
   * @param data
   * @returns {Promise<void>}
   */
  const onSubmit = async data => {
    const errorMessageElement = document.getElementById(
      "ffb-membership-form__error-message"
    );
    errorMessageElement.textContent = "";
    if (!errorMessageElement.classList.contains("visuallyhidden")) {
      errorMessageElement.classList.add("visuallyhidden");
    }
    const urlParams = getParams();
    const registerRedirectUrl = document.querySelector(
      ".ffb-membership-form__container"
    ).dataset.ffbRegisterRedirectUrl;
    setIsSubmitting(true);
    if (urlParams?.vid !== undefined) {
      data.Vid = urlParams.vid;
    }
    data.ReturUrl = registerRedirectUrl;
    try {
      const response = await apiFetch({
        path: "/ffb-membership-form/v1/create-member",
        method: "POST",
        data: data
      });
      if (response.error) {
        errorMessageElement.classList.remove("visuallyhidden");
        errorMessageElement.textContent = response.error;

		// 16 FEB 2024 - Alex N. - added a timeout to remove the error message after 3 seconds
	  	setTimeout	(function() {
			setIsSubmitting(false);
		},
			3000);
      }
      if (response.url) {
	  	// 16 FEB 2024 - Alex N. - when we redirect to the CMS page, we don't need the button to be active again. Make sure the button is not activated.
        localStorage.removeItem("ffb-membership-cta-phone-number");
        localStorage.removeItem("ffb-membership-type");
        // Redirect to CMS page.
        window.location.href = response.url;
      }
    } catch (e) {
      console.log(e);
      setIsSubmitting(false);
    } finally {
      localStorage.removeItem("ffb-membership-cta-phone-number");
      localStorage.removeItem("ffb-membership-type");
    }

	// 16 FEB 2024 - Alex N. - removing the general button enabling. This is treated above.
  };

  /**
   * Get query params from URL.
   *
   * @param url
   * @returns {{}}
   */
  function getParams(url = window.location) {
    // Create a params object
    let params = {};

    new URL(url).searchParams.forEach(function(val, key) {
      params[key] = val;
    });

    return params;
  }

  const Medlemskategori = watch("Medlemskategori");
  const SamtykkeGjensidige = watch("SamtykkeGjensidige");
  const SamtykkeNordea = watch("SamtykkeNordea");
  const GodkjMedVilk = watch("GodkjMedVilk");
  const ErVikar = watch("ErVikar");

  /**
   * Toggle section visibuity.
   *
   * @param event
   */
  const handleChangeType = event => {
    const group1 = document.querySelector(".ffb-membership-form__group1");
    const group2 = document.querySelector(".ffb-membership-form__group2");
    if (event.target.value === "STU") {
      group1.style.display = "none";
      group2.style.display = "block";
    } else if (event.target.value === "ORD") {
      group1.style.display = "block";
      group2.style.display = "none";

	  // 14 NOV 2023 - Alex N. - making sure that when the form is returned to Ordinary, the TypeProsess is set to LTR and is not defaulting to VFB (Vipps)
	  setValue("TypeProsess", "LTR");

      // Clear error when switch from 'STU' to 'ORD' and older than allowed age
      clearErrors("Personnr");
    }
  };

  /**
   * Get dropdown options for business.
   *
   * @param inputText
   * @returns {Promise<unknown>}
   */
  const getAsyncBusinessOptions = async inputText => {
    try {
      const response = await apiFetch({
        path: `/ffb-membership-form/v1/businesses/A/${inputText}`,
        method: "GET"
      });
      return response;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  /**
   * Debounced callback for React Select.
   *
   * @type {*}
   */
  const loadBusinessOptions = useCallback(
    debounce((inputText, callback) => {
      getAsyncBusinessOptions(inputText).then(options => callback(options));
    }, 500),
    []
  );

  /**
   * Get dropdown options for school.
   *
   * @param inputText
   * @returns {Promise<unknown>}
   */
  const getAsyncStudentOptions = async inputText => {
    try {
      const response = await apiFetch({
        path: `/ffb-membership-form/v1/businesses/S/${inputText}`,
        method: "GET"
      });
      return response;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  /**
   * Debounced callback for React Select.
   *
   * @type {*}
   */
  const loadStudentOptions = useCallback(
    debounce((inputText, callback) => {
      getAsyncStudentOptions(inputText).then(options => callback(options));
    }, 500),
    []
  );

	const [phoneValue, setPhoneValue] = useState('+47');
	const [country, setCountry] = useState('NO');

  const setPhoneCountryCode = (event, country) => {
	  if ( event.target.value.length === getCountryCallingCode(country).length ) {
		  setPhoneValue(`+${getCountryCallingCode(country)}`);
	  } else {
		  const pattern = /^\+?([0-9\b?\s]+)$/;
		  if ( pattern.test(event.target.value) ) {
			  if (!event.target.value.startsWith(`+${getCountryCallingCode(country)}`)) {
				  setPhoneValue(`+${getCountryCallingCode(country)}${event.target.value}`);
			  } else {
				  setPhoneValue(event.target.value);
			  }
		  }
	  }
  };

  const defaultSelectValue= [{
	value: 'NO',
	label: `+${getCountryCallingCode('NO')} ${nb['NO']}`
  }];

  const countryOptions = getCountries().map((countryCode) => ({
		  value: countryCode,
		  label: `+${getCountryCallingCode(countryCode)} ${nb[countryCode]}`
	  }));

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="ffb-membership-form__form"
      >
        <section className="ffb-membership-form__form-section">
          <h3 className="ffb-membership-form__form-section-heading">
            1. Velg type medlemskap
          </h3>
          <select
            id="Medlemskategori"
            {...register("Medlemskategori", {
              required: true,
              onChange: handleChangeType
            })}
            className={`ffb-membership-form__form-field ffb-membership-form--category`}
          >
            <option value="ORD">Ordinært medlemskap</option>
            <option value="STU">Studentmedlemskap</option>
          </select>
        </section>
        <section className="ffb-membership-form__form-section">
          <h3 className="ffb-membership-form__form-section-heading">
            2. Personopplysninger
          </h3>
			<div className="ffb-membership-form__form-field-container">
				<label htmlFor="Mobil" className="visuallyhidden">
					Telefonnummer
				</label>
				<div className="ffb-membership-form__phone-wrapper">
					<Select
						options={countryOptions}
						onChange={(NewCountryCode) => {
							setCountry(NewCountryCode.value);
							setPhoneValue(`+${getCountryCallingCode(NewCountryCode.value)}`);
						}}
						defaultValue={defaultSelectValue[0]}
						value={countryOptions.find(function (option) {
							return option.value === country;
						})}
						placeholder={`+${getCountryCallingCode(country)}`}
					/>
					<input
						className={classNames({
							"ffb-membership-form__form-field": true,
							"ffb-membership-form--phone": true,
							"ffb-membership-form__form-field--error": errors.mobile
						})}
						id="Mobil"
						placeholder="Telefonnummer (Misligholde: +47 xxx xx xxx)"
						type="tel"
						autoComplete="tel"
						value={phoneValue}
						{...register("mobile", {
							required: "Dette feltet er obligatorisk",
							validate: (value) => value && isValidPhoneNumber(value.toString()) ? true : "Ugyldig telefonnummer"
						})}
						onChange={(event) => {
							setPhoneCountryCode(event, country);
						}}
						onFocus={(event) => {
							event.target.value = phoneValue;
						}}
					/>
				</div>
				{errors.mobile && (
					<p className="ffb-membership-form__form-field-error">
						{errors.mobile.message}
					</p>
				)}
			</div>
			<div className="ffb-membership-form__form-field-container">
				<label htmlFor="Fornavn" className="visuallyhidden">
					Fornavn
				</label>
				<input
					className={classNames({
						"ffb-membership-form__form-field": true,
						"ffb-membership-form--fname": true,
                "ffb-membership-form__form-field--error": errors.fname
              })}
              id="Fornavn"
              placeholder="Fornavn"
              autoComplete="given-name"
              {...register("fname", {
                required: "Dette feltet er obligatorisk",
                minLength: {
                  value: 2,
                  message: "Minste lengde 2"
                }
              })}
            />
            {errors.fname && (
              <p className="ffb-membership-form__form-field-error">
                {errors.fname.message}
              </p>
            )}
          </div>
          <div className="ffb-membership-form__form-field-container">
            <label htmlFor="Etternavn" className="visuallyhidden">
              Etternavn
            </label>
            <input
              className={classNames({
                "ffb-membership-form__form-field": true,
                "ffb-membership-form--lname": true,
                "ffb-membership-form__form-field--error": errors.lname
              })}
              id="Etternavn"
              placeholder="Etternavn"
              autoComplete="family-name"
              {...register("lname", {
                required: "Dette feltet er obligatorisk",
                minLength: {
                  value: 2,
                  message: "Minste lengde 2"
                }
              })}
            />
            {errors.lname && (
              <p className="ffb-membership-form__form-field-error">
                {errors.lname.message}
              </p>
            )}
          </div>
          <div className="ffb-membership-form__form-field-container">
            <label htmlFor="Personnr" className="visuallyhidden">
              Ditt fødselsnummer (11 siffer)
            </label>
            <input
              className={classNames({
                "ffb-membership-form__form-field": true,
                "ffb-membership-form--personnr": true,
                "ffb-membership-form__form-field--error": errors.Personnr
              })}
              id="Personnr"
              placeholder="Ditt fødselsnummer (11 siffer)"
              type="number"
              {...register("Personnr", {
                required: "Dette feltet er obligatorisk",
                pattern: {
                  value: /\d{11}/,
                  message: "Ugyldig fødselsnummer"
                },
                validate: {
                /* Commented out the age validation.
                  isSTUorORD: val => {
                    let age;

                    const now = new Date();
                    let currentYear = now.getFullYear();
                    let day = parseInt(val.substr(0, 2));
                    let month = parseInt(val.substr(2, 2));
                    let year = parseInt(val.substr(4, 2));

                    // More regarding date of birth (Fødselsnummer)
                    // https://no.wikipedia.org/wiki/F%C3%B8dselsnummer#Oppbygning
                    if (year < currentYear - 2000) {
                      // Born on or after 2000!
                      year = year + 2000;
                      age = currentYear - year;
                    } else {
                      // Born before 2000!
                      year = year + 1900;
                      age = currentYear - year;
                    }

                    // If turns 30 this year, make sure birthday is not passed
                    if (age == 30) {
                      let birthday = new Date(year, month - 1, day);

                      const now_day_num = Math.ceil(
                        (now - new Date(now.getFullYear(), 0, 1)) / 86400000
                      );
                      const dob_day_num = Math.ceil(
                        (birthday - new Date(birthday.getFullYear(), 0, 1)) /
                          86400000
                      );

                      if (now_day_num < dob_day_num) {
                        age--;
                      }
                    }
                    if (
                      getValues("Medlemskategori") === "STU" &&
                      age > 29 &&
                      val.length == 11
                    ) {
                      return "Du kan ikke melde deg inn som studentmedlem etter at du har fylt 30 år. Meld deg inn som ordinært medlem eller ta kontakt med oss.";
                    }
                  },
                */
                  isMoreThan11Char: val =>
                    val.length > 11
                      ? "Fødselsnummer kan ikke være mer enn 11 siffer"
                      : true
                }
              })}
            />
            <details className="ffb-membership-form__details">
              <summary className="ffb-membership-form__summary">
                Hvorfor trenger vi dette
              </summary>
              <p className="ffb-membership-form__details-content">
                Medlemskontingenten er fradragsberettiget – derfor trenger vi
                ditt personnummer.
              </p>
            </details>
            {errors.Personnr && (
              <p className="ffb-membership-form__form-field-error">
                {errors.Personnr.message}
              </p>
            )}
          </div>
          <div className="ffb-membership-form__form-field-container">
            <label htmlFor="Epost" className="visuallyhidden">
              E-post
            </label>
            <input
              className={classNames({
                "ffb-membership-form__form-field": true,
                "ffb-membership-form--email": true,
                "ffb-membership-form__form-field--error": errors.email
              })}
              id="epost"
              placeholder="E-post"
              type="email"
              autoComplete="email"
              {...register("email", {
                required: "Dette feltet er obligatorisk",
                pattern: {
                  value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  message: "Ugyldig e-post addresse"
                }
              })}
            />
            <br />
            {errors.email && (
              <p className="ffb-membership-form__form-field-error">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="ffb-membership-form__form-field-container">
            <label htmlFor="Adresse" className="visuallyhidden">
              Adresse
            </label>
            <input
              placeholder="Adresse"
              autoComplete="street-address"
              className={classNames({
                "ffb-membership-form__form-field": true,
                "ffb-membership-form--address": true,
                "ffb-membership-form__form-field--error": errors.address
              })}
              id="Adresse"
              {...register("address", { required: true })}
            />
            {errors.address && (
              <p className="ffb-membership-form__form-field-error">
                {errors.address.message}
              </p>
            )}
          </div>
          <div className="ffb-membership-form__postwrapper">
            <div className="ffb-membership-form__form-field-container">
              <label htmlFor="Postnr" className="visuallyhidden">
                Postnr
              </label>
              <input
                className={classNames({
                  "ffb-membership-form__form-field": true,
                  "ffb-membership-form--postal": true,
                  "ffb-membership-form__form-field--error": errors.postal
                })}
                placeholder="Postnr"
                autoComplete="postal-code"
                {...register("postal", {
                  required: "Dette feltet er obligatorisk",
                  maxLength: {
                    value: 4,
                    message: "Max length is 4"
                  }
                })}
              />
              {errors.postal && (
                <p className="ffb-membership-form__form-field-error">
                  {errors.postal.message}
                </p>
              )}
            </div>
            <div className="ffb-membership-form__form-field-container">
              <label htmlFor="Poststed" className="visuallyhidden">
                Poststed
              </label>
              <input
                className={classNames({
                  "ffb-membership-form__form-field": true,
                  "ffb-membership-form--city": true,
                  "ffb-membership-form__form-field--error": errors.city
                })}
                id="Poststed"
                placeholder="Poststed"
                autoComplete="address-level2"
                {...register("city", {
                  required: "Dette feltet er obligatorisk",
                  minLength: {
                    value: 2,
                    message: "Minste lengde 2"
                  }
                })}
              />
              {errors.city && (
                <p className="ffb-membership-form__form-field-error">
                  {errors.city.message}
                </p>
              )}
            </div>
          </div>

          {Medlemskategori === "STU" && (
            <>
              <div className="ffb-membership-form__form-field-container">
                <label
                  htmlFor="Skole"
                  className="ffb-membership-form__study-label"
                >
                  Finn ditt utdanningssted
                </label>
                <Controller
                  name="Skole"
                  control={control}
                  render={({ field }) => (
                    <AsyncSelect
                      {...field}
                      className={classNames({
                        "ffb-membership-form__form-field--select-error":
                          errors.Skole
                      })}
                      placeholder="Søk her"
                      loadOptions={loadStudentOptions}
                      onInputChange={value => setSchoolQuery(value)}
                      cacheOptions
                      defaultOptions={[]}
                      noOptionsMessage={() => "Ingen valgt"}
                    />
                  )}
                  {...register("Skole", {
                    validate: {
                      hasSkoleFriTekst: val =>
                        !val && !getValues("SkoleFriTekst")
                          ? "Dette feltet er obligatorisk"
                          : true
                    }
                  })}
                />
                {errors.Skole && (
                  <p className="ffb-membership-form__form-field-error">
                    {errors.Skole.message}
                  </p>
                )}
                <details className="ffb-membership-form__details">
                  <summary className="ffb-membership-form__summary">
                    Mitt utdanningssted er ikke i listen.
                  </summary>
                  <div className="ffb-membership-form__form-field-container">
                    <label htmlFor="SkoleFriTekst" className="visuallyhidden">
                      SkoleFriTekst
                    </label>
                    <input
                      className="ffb-membership-form__form-field ffb-membership-form__form-field--outlined"
                      id="SkoleFriTekst"
                      placeholder="Fyll inn ditt utdanningssted"
                      {...register("SkoleFriTekst", {
                        minLength: {
                          value: 2,
                          message: "Minste lengde 2"
                        },
                        validate: {
                          filledSkoleFriTekst: val =>
                            !val && !getValues("Skole")
                              ? setError("Skole", {
                                  type: "error",
                                  message: "Dette feltet er obligatorisk"
                                })
                              : clearErrors("Skole")
                        }
                      })}
                    />
                    {errors.SkoleFriTekst && (
                      <p className="ffb-membership-form__form-field-error">
                        {errors.SkoleFriTekst.message}
                      </p>
                    )}
                  </div>
                </details>
              </div>
              <div className="ffb-membership-form__form-field-container">
                <input
                  className={classNames({
                    "ffb-membership-form__form-field": true,
                    "ffb-membership-form--year": true,
                    "ffb-membership-form__form-field--error": errors.Eksamensaar
                  })}
                  id="Eksamensaar"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Avgangsår"
                  {...register("Eksamensaar", {
                    required: "Dette feltet er obligatorisk",
                    minLength: {
                      value: 4,
                      message: "Årstall må ha 4 siffer"
                    },
                    maxLength: {
                      value: 4,
                      message: "Årstall kan kun ha 4 siffer"
                    }
                  })}
                />
                {errors.Eksamensaar && (
                  <p className="ffb-membership-form__form-field-error">
                    {errors.Eksamensaar.message}
                  </p>
                )}
              </div>
            </>
          )}
          <div className="ffb-membership-form__form-field-container">
            <label
              htmlFor="Arbgiver"
              className="ffb-membership-form--arbgiver-label"
            >
              Finn din arbeidsgiver
            </label>
            <Controller
              id="Arbgiver"
              name="Arbgiver"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  placeholder="Søk her"
                  loadOptions={loadBusinessOptions}
                  onInputChange={value => setBusinessQuery(value)}
                  cacheOptions
                  defaultOptions={[]}
                  noOptionsMessage={() => "Ingen valgt"}
                />
              )}
            />
            <details className="ffb-membership-form__details">
              <summary className="ffb-membership-form__summary">
                Mitt arbeidssted er ikke i listen.
              </summary>
              <div className="ffb-membership-form__form-field-container">
                <label htmlFor="ArbgiverFriTekst" className="visuallyhidden">
                  Arbeidsgiver
                </label>
                <input
                  className={classNames({
                    "ffb-membership-form__form-field": true,
                    "ffb-membership-form__form-field--outlined": true,
                    "ffb-membership-form--arbgiver-freetxt": true,
                    "ffb-membership-form__form-field--error":
                      errors.ArbgiverFriTekst
                  })}
                  id="ArbgiverFriTekst"
                  placeholder="Fyll inn din arbeidsgiver"
                  {...register("ArbgiverFriTekst", {
                    minLength: {
                      value: 2,
                      message: "Minste lengde 2"
                    }
                  })}
                />
                {errors.ArbgiverFriTekst && (
                  <p className="ffb-membership-form__form-field-error">
                    {errors.ArbgiverFriTekst.message}
                  </p>
                )}
              </div>
            </details>
            {Medlemskategori === "STU" && (
              <details className="ffb-membership-form__details">
                <summary className="ffb-membership-form__summary">
                  Hvorfor registrere arbeidsgiver?
                </summary>
                <p className="ffb-membership-form__details-content">
                  Ved å registrere arbeidsgiver får du som jobber i
                  finansnæringen et større nettverk, mulighet til å bli en del
                  av studentmiljøet eller bli studentambassadør i bedriften.
                </p>
              </details>
            )}
          </div>
          {Medlemskategori !== "STU" && (
            <div className="ffb-membership-form__form-field-container">
              <input
                id="ErVikar"
                type="checkbox"
                {...register("ErVikar")}
                className="ffb-membership-form__field-checkbox"
              />
              <label htmlFor="ErVikar">Jeg er vikar via vikarbyrå</label>
              {ErVikar && (
                <div>
                  <label htmlFor="BruttoLonn" className="visuallyhidden">
                    BruttoLonn
                  </label>
                  <input
                    className={classNames({
                      "ffb-membership-form__form-field": true,
                      "ffb-membership-form__form-field--error":
                        errors.BruttoLonn
                    })}
                    id="BruttoLonn"
                    placeholder="Oppgi brutto månedslønn."
                    {...register("BruttoLonn", {
                      required: "Dette feltet er obligatorisk",
                      minLength: {
                        value: 2,
                        message: "Minste lengde 2"
                      }
                    })}
                  />
                  {errors.BruttoLonn && (
                    <p className="ffb-membership-form__form-field-error">
                      {errors.BruttoLonn.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
        <section className="ffb-membership-form__form-section">
          <h3 className="ffb-membership-form__form-section-heading">
            3. Vilkår & Samtykker
          </h3>
          <div className="ffb-membership-form__form-field-container ffb-membership-form__form-field-container--underline">
            <input
              className="ffb-membership-form__field-checkbox"
              type="checkbox"
              id="SamtykkeGjensidige"
              {...register("SamtykkeGjensidige")}
            />
            <label htmlFor="SamtykkeGjensidige">
              Jeg ønsker rabatt hos Gjensidige Forsikring og Gjensidige
              Pensjonsforsikring og vil dele nødvendig informasjon med dem
            </label>
            <details className="ffb-membership-form__details">
              <summary className="ffb-membership-form__summary">
                Dette deler vi
              </summary>
              <p className="ffb-membership-form__details-content">
                Informasjonen som overføres inkluderer medlemsnummer,
                innmeldingsdato, fødselsnummer, fornavn, etternavn,
                bostedsadresse, mobilnummer samt eventuelt e-postadresse ved
                avgitt samtykke. Vi tar personvern på alvor og behandler dine
                personopplysninger på en trygg og forsvarlig måte.
              </p>
            </details>
            {SamtykkeGjensidige && (
              <>
                <p className="ffb-membership-form__small-text">
                  Jeg ønsker råd og markedsføring på:
                </p>
                <div className="ffb-membership-form__checks">
                  <div className="ffb-membership-form__form-field-container">
                    <input
                      className="ffb-membership-form__field-checkbox"
                      id="SamtykkeGjensidigeEpost"
                      type="checkbox"
                      {...register("SamtykkeGjensidigeEpost")}
                    />
                    <label htmlFor="SamtykkeGjensidigeEpost">E-post</label>
                  </div>
                  <div className="ffb-membership-form__form-field-container">
                    <input
                      className="ffb-membership-form__field-checkbox"
                      id="SamtykkeGjensidigeSMS"
                      type="checkbox"
                      {...register("SamtykkeGjensidigeSMS")}
                    />
                    <label htmlFor="SamtykkeGjensidigeSMS">SMS</label>
                  </div>
                  <div className="ffb-membership-form__form-field-container">
                    <input
                      className="ffb-membership-form__field-checkbox"
                      id="SamtykkeGjensidigeTelefon"
                      type="checkbox"
                      {...register("SamtykkeGjensidigeTelefon")}
                    />
                    <label htmlFor="SamtykkeGjensidigeTelefon">
                      Telefon fra Gjensidige Forsikring
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="ffb-membership-form__form-field-container ffb-membership-form__form-field-container--underline">
            <input
              className="ffb-membership-form__field-checkbox"
              id="SamtykkeNordea"
              type="checkbox"
              {...register("SamtykkeNordea")}
            />
            <label htmlFor="SamtykkeNordea">
              Jeg ønsker rabatt hos Nordea og vil dele nødvendig informasjon med
              dem.
            </label>
            <details className="ffb-membership-form__details">
              <summary className="ffb-membership-form__summary">
                Dette deler vi
              </summary>
              <p className="ffb-membership-form__details-content">
                Informasjonen som overføres inkluderer medlemsnummer,
                innmeldingsdato, fødselsnummer, fornavn, etternavn,
                bostedsadresse, mobilnummer samt eventuelt e-postadresse ved
                avgitt samtykke. Vi tar personvern på alvor og behandler dine
                personopplysninger på en trygg og forsvarlig måte.
              </p>
            </details>
            {SamtykkeNordea && (
              <>
                <p className="ffb-membership-form__small-text">
                  Jeg ønsker råd og markedsføring på:
                </p>
                <div className="ffb-membership-form__checks">
                  <div className="ffb-membership-form__form-field-container">
                    <input
                      className="ffb-membership-form__field-checkbox"
                      id="SamtykkeNordeaEpost"
                      type="checkbox"
                      {...register("SamtykkeNordeaEpost")}
                    />
                    <label htmlFor="SamtykkeNordeaEpost">E-post</label>
                  </div>
                  <div className="ffb-membership-form__form-field-container">
                    <input
                      className="ffb-membership-form__field-checkbox"
                      id="SamtykkeNordeaSMS"
                      type="checkbox"
                      {...register("SamtykkeNordeaSMS")}
                    />
                    <label htmlFor="SamtykkeNordeaSMS">SMS</label>
                  </div>
                  <div className="ffb-membership-form__form-field-container">
                    <input
                      className="ffb-membership-form__field-checkbox"
                      id="SamtykkeNordeaTelefon"
                      type="checkbox"
                      {...register("SamtykkeNordeaTelefon")}
                    />
                    <label htmlFor="SamtykkeNordeaTelefon">
                      Telefon fra Nordea
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="ffb-membership-form__form-field-container ffb-membership-form__form-field-container--underline">
            <input
              className="ffb-membership-form__field-checkbox"
              id="SamtykkeNyhetsbrev"
              type="checkbox"
              {...register("SamtykkeNyhetsbrev")}
            />
            <label htmlFor="SamtykkeNyhetsbrev">
              Jeg ønsker nyhetsbrev (Valgfri)
            </label>
          </div>
          <div className="ffb-membership-form__form-field-container ffb-membership-form__form-field-container--underline">
            <input
              className="ffb-membership-form__field-checkbox"
              id="SamtykkeSMS"
              type="checkbox"
              {...register("SamtykkeSMS")}
            />
            <label htmlFor="SamtykkeSMS">
              Finansforbundet kan sende meg SMS (Valgfri)
            </label>
          </div>
          <p className="ffb-membership-form__small-text">
            Disse valgene kan endres senere ved å logge inn på Min side
          </p>
        </section>
        {Medlemskategori === "STU" && (
          <section className="ffb-membership-form__form-section">
            <h3 className="ffb-membership-form__form-section-heading">
              4. Velg betalingsmetode
            </h3>
            <div className="ffb-membership-form__form-field-container">
              <input
                className="ffb-membership-form__field-radio"
                defaultChecked
                id="TypeProsess1"
                {...register("TypeProsess", { required: true })}
                type="radio"
                value="VFB"
              />
              <label htmlFor="TypeProsess1">
                <img
                  id="vipps"
                  src={VippsLogo}
                  alt="Vipps logo"
                  height="43px"
                  width="107px"
                />
                <span className="screen-reader-text">
                  VIPPS Faste betalinger
                </span>
              </label>
            </div>
            <div className="ffb-membership-form__form-field-container">
              <input
                className="ffb-membership-form__field-radio"
                id="TypeProsess2"
                {...register("TypeProsess", { required: true })}
                type="radio"
                value="FAK"
              />
              <label htmlFor="TypeProsess2">Faktura</label>
            </div>
          </section>
        )}
        <section className="ffb-membership-form__form-section ffb-membership-form__terms">
          <div className="ffb-membership-form__form-field-container">
            <input
              className="ffb-membership-form__field-checkbox"
              id="GodkjMedVilk"
              type="checkbox"
              {...register("GodkjMedVilk", { required: true })}
            />
            <label htmlFor="GodkjMedVilk">
              Jeg har lest og forstått medlemsvilkårene
            </label>
            <p className="ffb-membership-form__small-text ffb-membership-form__small-text--alt">
              <a href="https://www.finansforbundet.no/medlemskap/vilkar-for-medlemskap/">
                Les medlemsvilkårene
              </a>{" "}
            </p>
          </div>
          <input
            type="submit"
            value="Send inn skjema"
            disabled={isSubmitting || !GodkjMedVilk}
            className="ffb-membership-form__form-submit"
          />
        </section>
      </form>
      <div id="ffb-membership-form__error-container">
        <p
          id="ffb-membership-form__error-message"
          className="visuallyhidden"
        ></p>
      </div>
    </>
  );
}
